import React, { useEffect, useRef } from 'react';
import { icons } from '../icons';

type KoobDrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Beside the title, before the close button (a timer, a status). */
  actions?: React.ReactNode;
  /** Under the title, full width (tabs). */
  header?: React.ReactNode;
  /** Bottom bar (totals, actions). */
  footer?: React.ReactNode;
  widthClass?: string;
  children?: React.ReactNode;
};

// A side panel over the page that, unlike KoobSlideOver, is not modal: the
// app sidebar stays above its scrim (`z-drawer`) and usable, and focus is not
// trapped. Opening moves focus into the panel, closing hands it back to what
// had it. Escape (pressed in the panel, not in a menu opened from it) or a
// click on the scrim closes it.
export default function KoobDrawer({
  open,
  onClose,
  title,
  actions,
  header,
  footer,
  widthClass = 'max-w-[620px]',
  children,
}: KoobDrawerProps) {
  const panel = useRef<HTMLElement>(null);
  // Callers pass a new onClose each render: it must not move the focus again.
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const active = document.activeElement;
      if (
        active &&
        active !== document.body &&
        !panel.current?.contains(active)
      ) {
        return;
      }
      close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-drawer bg-k-h/35 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <section
        ref={panel}
        role="dialog"
        tabIndex={-1}
        className={`fixed inset-y-0 right-0 z-drawer flex w-full ${widthClass} flex-col bg-white shadow-xl outline-none`}
      >
        <header className="flex items-start gap-3.5 border-b border-solid border-x-0 border-t-0 border-zinc-200 px-[22px] py-[18px]">
          <div className="min-w-0 flex-1">{title}</div>
          {actions}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-solid border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-100"
          >
            <i className={icons.close} />
          </button>
        </header>
        {header && (
          <div className="border-b border-solid border-x-0 border-t-0 border-zinc-200 px-[22px]">
            {header}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-[22px] py-5">{children}</div>
        {footer && (
          <footer className="border-t border-solid border-x-0 border-b-0 border-zinc-200 bg-zinc-100 px-[22px] py-3.5">
            {footer}
          </footer>
        )}
      </section>
    </>
  );
}
